/**
 * A legal document (privacy policy, terms of service) in the public frame. No
 * Figma frame yet, so it follows the Settings page: the contents as a list of
 * rows on the ground (sticky beside the document at desktop, folded above it on
 * tablet and phone), the document as one raised r16 sheet. The row being read
 * follows the scroll.
 */
import { useRef, useState, type ReactNode } from "react";
import { Element, Link as ScrollLink } from "react-scroll";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/helpers";
import { PublicFrame } from "@/components/gettingIn/PublicFrame";
import "./legal.css";

export interface LegalSection {
  id: string;
  title: string;
  content: ReactNode;
}

/** A picked section lands this far under the top of the window, and counts as read from there. */
const SCROLL_OFFSET = -24;

function Contents({
  sections,
  active,
  onActive,
  onPick,
}: {
  sections: LegalSection[];
  active: string;
  onActive: (id: string) => void;
  onPick?: () => void;
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {sections.map((section, index) => (
        <li key={section.id}>
          <ScrollLink
            to={section.id}
            href={`#${section.id}`}
            smooth
            duration={500}
            spy
            offset={SCROLL_OFFSET}
            onSetActive={() => onActive(section.id)}
            onClick={onPick}
            aria-current={active === section.id ? "location" : undefined}
            className={cn(
              "unstyled-link flex min-h-[34px] cursor-pointer items-start gap-2 rounded-tip px-3 py-2 text-[13px] leading-[18px] transition-colors",
              active === section.id
                ? "bg-co-hover font-semibold text-co-text"
                : "font-medium text-co-text2 hover:bg-co-hover hover:text-co-text",
            )}
          >
            <span className="w-5 shrink-0 tabular-nums text-co-muted">{index + 1}</span>
            <span className="min-w-0 flex-1">{section.title}</span>
          </ScrollLink>
        </li>
      ))}
    </ul>
  );
}

export function LegalDocument({
  title,
  updated,
  intro,
  sections,
}: {
  title: string;
  /** "Updated July 12, 2026", under the title. */
  updated?: string;
  intro: ReactNode;
  sections: LegalSection[];
}) {
  const [active, setActive] = useState("");
  const foldRef = useRef<HTMLDetailsElement>(null);

  return (
    <PublicFrame>
      <div className="mx-auto flex w-full max-w-[1200px] items-start gap-10 px-6 pt-6 pb-16 max-lg:flex-col max-lg:gap-4 max-sm:px-3 max-sm:pt-2">
        <nav
          aria-label="Contents"
          className="sticky top-6 max-h-[calc(100dvh-48px)] w-[260px] shrink-0 overflow-y-auto max-lg:hidden"
        >
          <p className="px-3 pb-2 text-[12px] font-semibold leading-4 text-co-muted">Contents</p>
          <Contents sections={sections} active={active} onActive={setActive} />
        </nav>

        <details ref={foldRef} className="group w-full rounded-panel bg-co-surface lg:hidden">
          <summary className="flex h-12 cursor-pointer list-none items-center gap-2 rounded-panel px-4 [&::-webkit-details-marker]:hidden">
            <span className="flex-1 text-[14px] font-semibold text-co-text">Contents</span>
            <span className="text-[13px] font-medium text-co-muted">{sections.length} sections</span>
            <ChevronDown
              aria-hidden
              className="size-4 text-co-muted transition-transform duration-200 group-open:rotate-180"
            />
          </summary>
          <nav aria-label="Contents" className="px-1.5 pb-2">
            <Contents
              sections={sections}
              active={active}
              onActive={setActive}
              onPick={() => {
                if (foldRef.current) foldRef.current.open = false;
              }}
            />
          </nav>
        </details>

        <article className="legal-doc min-w-0 flex-1 rounded-panel bg-co-surface p-12 max-lg:w-full max-sm:px-5 max-sm:py-7">
          <h1 className="gi-title">{title}</h1>
          {updated ? <p className="mt-3 text-[13px] font-medium leading-[18px] text-co-muted">{updated}</p> : null}
          <div className="mt-6 text-[15px] leading-6 text-co-text2">{intro}</div>
          <div className="mt-12 flex flex-col gap-10">
            {sections.map((section, index) => (
              <Element key={section.id} name={section.id} id={section.id}>
                <h2 className="text-[18px] leading-6 text-co-text">
                  <span className="text-co-muted">{index + 1}.</span> {section.title}
                </h2>
                <div className="mt-3 text-[15px] leading-6 text-co-text2">{section.content}</div>
              </Element>
            ))}
          </div>
        </article>
      </div>
    </PublicFrame>
  );
}
